import React, {useState, useEffect, createContext, useRef} from 'react';
import axios from "./axios";
import {useSnackbar} from 'notistack';
import useInterval from "../hooks/useInterval";
import Snackbar from "../components/Snackbar";

const TemplateContext = createContext([{}, () => {}]);
const SESSION_EXPIRED_WARNING_TIME = 21480000;
const SESSION_EXPIRED_TIME = 21610000;

const TemplateProvider = (props) => {
    const {children, shortcutId, initSinglePage} = props;
    const defaultIconComponentName = undefined;
    const defaultHeaderComponentName = undefined;
    const defaultTitleComponentName = 'DefaultTitle';
    const defaultBodyComponentName = 'DynamicContent';
    const defaultFooterComponentName = 'DefaultFooter';
    const [iconComponentName, setIconComponentName] = useState(defaultIconComponentName);
    const [headerComponentName, setHeaderComponentName] = useState(defaultHeaderComponentName);
    const [titleComponentName, setTitleComponentName] = useState(defaultTitleComponentName);
    const [bodyComponentName, setBodyComponentName] = useState(defaultBodyComponentName);
    const [footerComponentName, setFooterComponentName] = useState(defaultFooterComponentName);
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const [params, setParams] = useState();
    const [shortcut, setShortcut] = useState();
    const [shortcutClass, setShortcutClass] = useState();
    const [shortcutLocation, setShortcutLocation] = useState();
    const [url, setUrl] = useState();
    const [name, setName] = useState();
    const [navigationLinks, setNavigationLinks] = useState([]);
    const [singlePage, setSinglePage] = useState(initSinglePage);
    const [isSeriesNavVisible, setSeriesNavVisible] = useState(false);
    const [sessionExpiredWarning, setSessionExpiredWarning] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [shortcutLoading, setShortcutLoading] = useState(false);
    let sessionExpiredInterval = undefined;

    const loadShortcut = (shortcutId, params) => {
        setShortcutLoading(true);
        axios.get('/web/ui/shortcut/' + shortcutId)
            .then(response => {
                if(response.data.singlePage) {
                    setParams(params);
                    setShortcut(response.data);
                }
            }).catch(error =>{
                console.error(error);
                showSnackbar('Error', 'There has been a network error', 'error');
            }).finally(() => {
                setShortcutLoading(false);
            });
    }

    useEffect(() => {
        if(shortcutId && !isNaN(shortcutId)) {
            loadShortcut(shortcutId);
        }
    }, []);

    useEffect(() => {
        if(shortcut === undefined) {
            return;
        }

        setName(shortcut.name);
        setUrl(shortcut.url);
        setIconComponentName(shortcut.iconComponent);
        setTitleComponentName(shortcut.titleComponent);
        setBodyComponentName(shortcut.bodyComponent);
        setShortcutClass(shortcut.shortcutClass);
        setShortcutLocation(shortcut.shortcutLocation);
        setNavigationLinks([]);

        if (window.location.search !== '') {
            let shortcutParams = new URLSearchParams(window.location.search),
                shortcutParamsObj = Object.fromEntries([...shortcutParams]);
            setParams(shortcutParamsObj);
        }
    }, [shortcut]);

    const checkExpiredSession = () => {
        const networkDate = sessionStorage.getItem('networkDate');
        if(networkDate != undefined && networkDate > 0) {
            const sessionTime = Date.now() - Number(networkDate);
            if(!sessionExpiredWarning && sessionTime >= SESSION_EXPIRED_WARNING_TIME) {
                setSessionExpiredWarning(true);
            }

            if(!sessionExpired && sessionTime >= SESSION_EXPIRED_TIME) {
                axios.get('/web/ui/user/public/sessionLastAccessedTime')
                    .then(response => {
                        if(response.data !== undefined && response.data <= 0) {
                            setSessionExpired(true);
                            if(sessionExpiredInterval && sessionExpiredInterval.current) {
                                window.clearInterval(sessionExpiredInterval.current);
                            }
                        } else if(response.data !== undefined && response.data > 0) {
                            sessionStorage.setItem('networkDate', response.data);
                        } else if(sessionExpiredInterval && sessionExpiredInterval.current) {
                            window.clearInterval(sessionExpiredInterval.current);
                        }
                    }).catch(error => {
                        console.error(error);
                        if(sessionExpiredInterval && sessionExpiredInterval.current) {
                            window.clearInterval(sessionExpiredInterval.current);
                        }
                    });
            }
        }

    }

    sessionExpiredInterval = useInterval(checkExpiredSession, 60000);

    const hideSnackbar = (lastActiveElement) => {
        closeSnackbar();
        if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
            lastActiveElement.focus();
        }
    };

    const showSnackbar = (title, message, variant = 'success', persist = false, autoHideDuration = 4000, displayLoader = false) => {
        // variant could be success, error, warning, info, or default
        const lastActiveElement = document.activeElement;
        enqueueSnackbar(<Snackbar title={title} message={message} displayLoader={displayLoader} persist={persist} lastActiveElement={lastActiveElement} onClose={hideSnackbar}/>, {
            variant: variant,
            persist: persist,
            autoHideDuration: autoHideDuration,
            className: 'ch-snackbar-container',
            anchorOrigin: { horizontal: 'right', vertical: 'top' }
        });
    };

    if(globalReactFunctions !== undefined) {
        globalReactFunctions.loadShortcut = loadShortcut;
        globalReactFunctions.setNavigationLinks = setNavigationLinks;
        globalReactFunctions.showSnackbar = showSnackbar;
        globalReactFunctions.hideSnackbar = hideSnackbar;
    }

    const contextValue =  {
        iconComponentName, setIconComponentName,
        headerComponentName, setHeaderComponentName,
        titleComponentName, setTitleComponentName,
        bodyComponentName, setBodyComponentName,
        footerComponentName, setFooterComponentName,
        shortcut, setShortcut,
        shortcutClass, setShortcutClass,
        shortcutLocation, setShortcutLocation,
        params, setParams,
        name, setName,
        url, setUrl,
        navigationLinks, setNavigationLinks,
        singlePage, setSinglePage,
        sessionExpired,
        isSeriesNavVisible, setSeriesNavVisible,
        shortcutLoading, setShortcutLoading,
        showSnackbar
    }

    return (
        <TemplateContext.Provider value={contextValue}>
            {children}
        </TemplateContext.Provider>
    );
}

export {TemplateContext, TemplateProvider};